
import axios from 'axios';
import { getApiBaseUrl } from "../utils/urlHandler";
import { CognitoAuthService } from './cognitoService';
import { debug } from "../utils/debugLogger";

const BASE_URL = getApiBaseUrl("REACT_APP_AGENT_API_URL");

debug.log(`Using Agent API URL (${process.env.REACT_APP_ENV}):`, BASE_URL);

// Agent creation response interface
export interface CreateAgentResponse {
  ai_agent_id?: string;
  openai_assistant_id?: string;
  agent_name?: string;
  model?: string;
  instructions?: string;
  message?: string;
  [key: string]: any;
}

export const createAgent = async (
  agentData: {
    agent_name: string;
    instructions: string;
    model: string;
    provider: string;
    creativity?: number;
    files?: Array<{ bucket: string; key: string; filename: string }>;
    customer_id: string;
    profile_picture_url?: string;
    conversation_starters?: Array<{
      id: string;
      suggestion: string;
    }>;
    tools?: string[];
    api_version?: string;
    /** Agent type: content_creator (default) or ugc_video. Backend stores and uses for routing. */
    agent_type?: string;
  },
  idToken: string
): Promise<{ status: number; data: CreateAgentResponse }> => {
  try {
    const response = await axios.post(BASE_URL, agentData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      // Allow 504 to pass through so we can handle it
      validateStatus: (status) => (status >= 200 && status < 500) || status === 504,
    });
    
    // If status is 504, throw specific error
    if (response.status === 504) {
      const timeoutError = new Error("GATEWAY_TIMEOUT: Agent is being created in background");
      (timeoutError as any).isGatewayTimeout = true;
      throw timeoutError;
    }
    
    return { status: response.status, data: response.data };
  } catch (error: any) {
    debug.error("Error creating agent:", error);
    debug.error("Error response status:", error.response?.status);
    debug.error("Error response:", error.response);
    
    // Handle 504 Gateway Timeout - agent may still be creating in background.
    // Also covers CORS-blocked responses (ERR_NETWORK / ERR_FAILED) which happen when
    // API Gateway returns a 504 without CORS headers, preventing the browser from reading it.
    const isTimeout = error.response?.status === 504 || error?.isGatewayTimeout || error?.message?.includes("GATEWAY_TIMEOUT");
    const isCorsBlockedTimeout = !error.response && (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error'));
    if (isTimeout || isCorsBlockedTimeout) {
      const timeoutError = new Error("GATEWAY_TIMEOUT: Agent is being created in background");
      (timeoutError as any).isGatewayTimeout = true;
      throw timeoutError;
    }

    throw new Error("Failed to create agent. Please try again.");
  }
};

/**
 * Clones an existing agent. The backend reuses the create endpoint: a POST whose
 * body carries `clone_from_aiagent_id` duplicates the source agent's S3 training
 * files + DynamoDB record into a brand-new agent, applying any provided overrides.
 * Mirrors `createAgent`'s 504 handling since the clone copies files server-side.
 */
export const cloneAgent = async (
  payload: {
    clone_from_aiagent_id: string;
    customer_id: string;
    agent_name?: string;
    instructions?: string;
  },
  idToken: string
): Promise<{ status: number; data: CreateAgentResponse }> => {
  try {
    const response = await axios.post(BASE_URL, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      // Allow 504 through so we can treat it as "still cloning in background".
      validateStatus: (status) => (status >= 200 && status < 500) || status === 504,
    });

    if (response.status === 504) {
      const timeoutError = new Error("GATEWAY_TIMEOUT: Agent is being cloned in background");
      (timeoutError as any).isGatewayTimeout = true;
      throw timeoutError;
    }

    return { status: response.status, data: response.data };
  } catch (error: any) {
    debug.error("Error cloning agent:", error);

    const isTimeout =
      error.response?.status === 504 ||
      error?.isGatewayTimeout ||
      error?.message?.includes("GATEWAY_TIMEOUT");
    const isCorsBlockedTimeout =
      !error.response &&
      (error?.code === "ERR_NETWORK" || error?.message?.includes("Network Error"));
    if (isTimeout || isCorsBlockedTimeout) {
      const timeoutError = new Error("GATEWAY_TIMEOUT: Agent is being cloned in background");
      (timeoutError as any).isGatewayTimeout = true;
      throw timeoutError;
    }

    // Surface the backend message when present (e.g. a non-bedrock provider 400).
    throw new Error(error.response?.data?.message || "Failed to clone agent. Please try again.");
  }
};

export const fetchAgents = async (idToken: string) => {
  try {
    const response = await axios.get(BASE_URL, {
      headers: {
        Authorization: `Bearer ${idToken}`, // <-- Add Bearer prefix
      },
    });
    const agents = response.data.map((agent: any) => ({
      agent_id: agent.aiagent_id,
      agent_name: agent.agent_name,
    }));
    return agents;
  } catch (error) {
    debug.error("Error fetching agents:", error);
    throw new Error("Failed to fetch agents");
  }
};



/**
 * Fetches agent details using the agent_id query parameter.
 * @param agentId The ID of the agent to fetch.
 * @returns The agent details object, including openai_assistant_id.
 */
/**
 * Fetches agent details using the agent_id and customer_id query parameters.
 * @param agentId The ID of the agent to fetch.
 * @param customerId The customer ID to filter agent.
 * @param idToken The Cognito ID token for authorization.
 * @returns The agent details object, including openai_assistant_id.
 */
export const fetchAgentDetails = async (
  agentId: string,
  customerId: string,
  idToken: string
) => {
  try {
    // Validate that agentId is provided
    if (!agentId || agentId.trim() === "") {
      throw new Error("Agent ID is required to fetch agent details");
    }
    
    debug.log("fetchAgentDetails called with:", { agentId, customerId });
    
    const response = await axios.get(BASE_URL, {
      params: { agent_id: agentId, customer_id: customerId },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    debug.log("fetchAgentDetails response:", response.data);
    
    // Handle both array and single object responses
    const data = response.data;
    if (Array.isArray(data)) {
      if (data.length === 0) {
        throw new Error("Agent not found");
      }
      return data[0]; // Return first agent if array
    }
    return data; // Return single object
  } catch (error) {
    debug.error("Error fetching agent details:", error);
    throw new Error("Failed to fetch agent details.");
  }
};


/**
 * Fetches all agents for a specific customer by customer_id.
 * @param customerId The customer ID to filter agents.
 * @param idToken The Cognito ID token for authorization.
 * @returns Array of agents for the customer.
 */
export const fetchAgentsByCustomer = async (customerId: string, idToken: string) => {
  try {
    const response = await axios.get(BASE_URL, {
      params: { customer_id: customerId },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    // Return the full agent data - the API now returns complete agent information
    return response.data;
  } catch (error) {
    debug.error("Error fetching agents by customer:", error);
    throw new Error("Failed to fetch agents by customer");
  }
};

/**
 * Deletes an agent.
 *
 * The backend soft-deletes: the agent is immediately hidden from every read
 * endpoint, while the purge of its knowledge base (OpenAI vector store/files)
 * and S3 training files runs asynchronously. It therefore answers 202, not 200
 * — the agent is gone from the user's point of view the moment this resolves.
 *
 * Idempotent: deleting an already-deleted agent answers 200 rather than an
 * error, so a retry after a flaky connection is safe.
 *
 * @param agentId The ID of the agent to delete.
 * @param customerId The customer that owns the agent.
 * @param idToken The Cognito ID token for authorization.
 */
export const deleteAgent = async (
  agentId: string,
  customerId: string,
  idToken: string
) => {
  if (!agentId || agentId.trim() === "") {
    throw new Error("Agent ID is required to delete an agent");
  }
  if (!customerId || customerId.trim() === "") {
    throw new Error("Customer ID is required to delete an agent");
  }

  try {
    const response = await axios.delete(BASE_URL, {
      params: { agent_id: agentId, customer_id: customerId },
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    debug.log("deleteAgent response:", response.data);
    return response.data;
  } catch (error) {
    debug.error("Error deleting agent:", error);
    // Surface the backend's message when there is one — it distinguishes
    // "not found" and "not authorised" from a generic failure, which the
    // confirmation dialog shows to the user.
    if (axios.isAxiosError(error)) {
      const message = (error.response?.data as { message?: string } | undefined)?.message;
      if (message) throw new Error(message);
    }
    throw new Error("Failed to delete agent.");
  }
};

/**
 * Fetches conversation starters for a specific agent.
 * @param agentId The agent ID to fetch conversation starters for.
 * @param customerId The customer ID (kept for backward compatibility, not used in request).
 * @param idToken The Cognito ID token for authorization.
 * @returns Array of conversation starters.
 */
export const fetchAgentConversationStarters = async (
  agentId: string,
  customerId: string,
  idToken: string
) => {
  try {
    const response = await axios.get(`${BASE_URL}/${agentId}/conversation-starters`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return response.data;
  } catch (error) {
    debug.error("Error fetching conversation starters:", error);
    // Return empty array if backend fails - no fallback starters
    return [];
  }
};

/**
 * Updates an existing agent's instructions
 * @param aiagentId The ID of the agent to update
 * @param customerId The customer ID associated with the agent
 * @param assistantId The OpenAI assistant ID
 * @param agentName The agent name
 * @param instructions The new instructions for the agent
 * @param provider The provider (default: "openai")
 * @param idToken The Cognito ID token for authorization
 * @param tools Optional array of tools to enable for the agent
 * @param profilePictureUrl Optional profile picture URL
 * @param apiVersion Optional API version
 * @param model Optional model selection
 * @param conversationStarters Optional conversation starters
 * @returns The updated agent details
 */
export const updateAgentInstructions = async (
  aiagentId: string,
  customerId: string,
  assistantId: string | undefined,
  agentName: string,
  instructions: string,
  provider: string = "openai",
  idToken: string,
  tools?: string[],
  profilePictureUrl?: string,
  apiVersion?: string,
  model?: string,
  conversationStarters?: Array<{ id: string; suggestion: string }>
) => {
  try {
    const requestBody: any = {
      aiagent_id: aiagentId,
      customer_id: customerId,
      agent_name: agentName,
      instructions: instructions,
      provider: provider,
    };

    // Only include assistant_id if provided
    if (assistantId) {
      requestBody.assistant_id = assistantId;
    }

    // Include tools if provided
    if (tools && tools.length > 0) {
      requestBody.tools = tools;
    }

    // Include profile picture URL if provided
    if (profilePictureUrl !== undefined) {
      requestBody.profile_picture_url = profilePictureUrl;
    }

    // Include API version if provided
    if (apiVersion) {
      requestBody.api_version = apiVersion;
    }

    // Include model if provided. `!== undefined`, not truthiness: absent means
    // "leave alone", and an explicit value — even a falsy-looking one — is sent
    // (same class of bug as SGL-255's conversation starters).
    if (model !== undefined) {
      requestBody.model = model;
    }

    // Include conversation starters if provided
    if (conversationStarters && conversationStarters.length > 0) {
      requestBody.conversation_starters = conversationStarters;
    }

    const response = await axios.put(
      BASE_URL,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    debug.log("Agent updated successfully:", response.data);
    return response.data;
  } catch (error) {
    debug.error("Error updating agent instructions:", error);
    // Keep the server's reason (e.g. a 403 tenant-gate message) instead of hiding it.
    const serverMessage = axios.isAxiosError(error)
      ? error.response?.data?.message || error.message
      : undefined;
    throw new Error(serverMessage || "Failed to update agent instructions");
  }
};

export interface AgentTrainingFileRef {
  bucket: string;
  key: string;
  filename?: string;
}

/**
 * Updates agent training files using add_files / remove_files.
 * Backend expects PUT /ai-agents with:
 * { action: "update_files", customer_id, aiagent_id, add_files?, remove_files? }.
 * Returns a job_id for async processing (202 response) or direct result (200 response).
 */
export const updateAgentTrainingFiles = async (
  payload: {
    customer_id: string;
    aiagent_id: string;
    add_files?: AgentTrainingFileRef[];
    remove_files?: Array<{ bucket: string; key: string }>;
  },
  idToken: string
): Promise<{ job_id: string; status: string; message: string }> => {
  try {
    const response = await axios.put(
      BASE_URL,
      { action: "update_files", ...payload },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        validateStatus: (status) => status === 202 || status === 200, // Accept 202 for async jobs
      }
    );
    
    // Backend returns 202 with job_id for async processing
    if (response.status === 202) {
      if (!response.data.job_id) {
        throw new Error("Job ID not received from backend");
      }
      return {
        job_id: response.data.job_id,
        status: response.data.status || "PENDING",
        message: response.data.message || "File update job accepted.",
      };
    }
    
    // Handle 200 for backward compatibility if needed
    // If backend returns 200 directly, treat as immediate success
    return {
      job_id: "",
      status: "SUCCEEDED",
      message: "Files updated successfully",
    };
  } catch (error: any) {
    debug.error("Error updating agent training files:", error);
    if (error.response?.status === 202 && error.response?.data?.job_id) {
      // Handle case where axios might reject 202
      return {
        job_id: error.response.data.job_id,
        status: error.response.data.status || "PENDING",
        message: error.response.data.message || "File update job accepted.",
      };
    }
    throw new Error("Failed to update agent training files");
  }
};

/** One selectable model for agent creation / editing. */
export interface AgentModelOption {
  id: string;
  name: string;
  provider: string;
  providerLabel: string;
  /** Optional one-word tier hint, shown after the name in the picker (e.g. "fastest"). */
  hint?: string;
}

/**
 * Single source of truth for agent models. The FIRST entry is the default used when creating
 * new agents (onboarding + create form). To change the default or add a model, edit only this
 * list.
 *
 * Display strings and tier hints match CHAT_MODEL_OPTIONS (the chat picker) in
 * ChatWithAgent/config/modelOptions.ts, but the two lists are NOT the same set — the
 * differences below are pre-existing and were left alone deliberately:
 *  - Perplexity (sonar-*) is chat-only; agents cannot be created on that provider.
 *  - Chat additionally offers Opus 4.7 and GPT-5.1; this list additionally offers GPT-5.4.
 *    Reconciling that is a product decision, not a refactor — don't "fix" it here.
 *  - The two lists use different field names (`id`/`name` here, `model`/`label` there).
 *    Unifying them means touching NewChatWindow, CreateAgentForm, OnboardingAgentPage and
 *    ModelSelectionSection, so it is deliberately left as a follow-up.
 *
 * @param _idToken The Cognito ID token (reserved for future API implementation)
 */
export const getAvailableModels = async (
  _idToken?: string
): Promise<AgentModelOption[]> => {
  return [
    // Claude (Bedrock) — Sonnet 5 stays FIRST: it is the default for new Agents except the
    // Visibility Manager (Opus 5). See DEFAULT_MODEL_BY_AGENT_TYPE in
    // ChatWithAgent/config/modelOptions.ts (SGL-315, 2026-09-16). Auto (SGL-368) is listed
    // right after it, deliberately NOT first here — unlike the chat picker's CHAT_MODEL_OPTIONS,
    // this list's first entry IS the create-time default, and new Agents keep Sonnet 5 stored
    // (opt-in only) until a customer selects Auto for this agent themselves.
    // No "recommended" hint lives on any entry: which model is recommended depends on the
    // Agent type (Sonnet 5, or Opus 5 for the Visibility Manager), so the picker works it out
    // from defaultModelForAgentType. Retired models (RETIRED_MODEL_IDS) are not listed (SGL-423).
    { id: "claude-sonnet-5", name: "Sonnet 5", provider: "bedrock_claude", providerLabel: "Claude" },
    { id: "auto", name: "Auto", provider: "bedrock_claude", providerLabel: "Claude" },
    { id: "claude-opus-5", name: "Opus 5", provider: "bedrock_claude", providerLabel: "Claude" },
    { id: "claude-fable-5-1", name: "Fable 5.1", provider: "bedrock_claude", providerLabel: "Claude", hint: "premium" },
    { id: "claude-opus-4-8", name: "Opus 4.8", provider: "bedrock_claude", providerLabel: "Claude" },
    { id: "claude-haiku-4-5-20251001", name: "Haiku 4.5", provider: "bedrock_claude", providerLabel: "Claude", hint: "fastest" },
    // OpenAI — existing models stay listed; GPT-5.6 ships as three separate tiers.
    { id: "gpt-6-astra", name: "GPT-6 Astra", provider: "openai", providerLabel: "GPT", hint: "premium" },
    { id: "gpt-5.4", name: "GPT-5.4", provider: "openai", providerLabel: "GPT" },
    { id: "gpt-5.6-luna", name: "GPT-5.6 Luna", provider: "openai", providerLabel: "GPT", hint: "fastest" },
    { id: "gpt-5.6-terra", name: "GPT-5.6 Terra", provider: "openai", providerLabel: "GPT", hint: "balanced" },
    { id: "gpt-5.6-sol", name: "GPT-5.6 Sol", provider: "openai", providerLabel: "GPT", hint: "premium" },
  ];
};

/**
 * Regenerates caption for a post using AI agent chat endpoint
 * @param payload The payload containing assistant_id and content to regenerate
 * @param idToken The Cognito ID token for authorization
 * @returns The regenerated caption
 */
export const regenerateCaption = async (
  payload: any,
  idToken: string
): Promise<{ content: string }> => {
  // Use the chat endpoint for caption regeneration
  const endpoint = `${BASE_URL}/chat`;
  debug.log(`[REGENERATE] Calling API: ${endpoint}`);
  
  try {
    // Extract assistant_id and caption from the payload
    const assistantId = payload.assistant_id;
    const message = payload.message || "";
    
    if (!assistantId) {
      throw new Error('No assistant_id provided in payload');
    }
    
    // Create the chat payload - ONLY include the required fields
    const chatPayload = {
      message: message,
      assistant_id: assistantId
    };
    
    debug.log("[REGENERATE] Sending chat request with payload:", chatPayload);
    
    // Make the API call
    const response = await axios.post(
      endpoint,
      chatPayload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      }
    );
    
    if (response.status !== 200) {
      throw new Error(`Failed to regenerate caption. Status: ${response.status}`);
    }
    
    debug.log(`[REGENERATE] API response:`, response.data);
    
    // Extract content from the response - handle different response formats
    const regeneratedCaption = response.data.content || response.data.message || response.data || "";
    
    // If regeneratedCaption is an object, try to extract content or message from it
    const content = typeof regeneratedCaption === 'object' 
      ? regeneratedCaption.content || regeneratedCaption.message || JSON.stringify(regeneratedCaption)
      : regeneratedCaption;
    
    return {
      content: content
    };
  } catch (error: any) {
    debug.error('[REGENERATE] API error:', error);
    
    // Check for CORS-related errors
    if (error.message && error.message.includes('Network Error')) {
      debug.error('[REGENERATE] Possible CORS error detected');
      throw new Error(
        'Network error: Unable to connect to the API server. This may be due to CORS restrictions or network connectivity issues.'
      );
    }
    
    // Log more details about the error for debugging
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      debug.error('[REGENERATE] Error response data:', error.response.data);
      debug.error('[REGENERATE] Error response status:', error.response.status);
      debug.error('[REGENERATE] Error response headers:', error.response.headers);
      
      // Throw a more specific error with the actual error message from the API
      const errorMessage = error.response.data?.message || error.response.data || 'Unknown API error';
      throw new Error(`API Error (${error.response.status}): ${errorMessage}`);
    } else if (error.request) {
      // The request was made but no response was received
      debug.error('[REGENERATE] Error request:', error.request);
      throw new Error('No response received from API server');
    } else {
      // Something happened in setting up the request that triggered an Error
      debug.error('[REGENERATE] Error message:', error.message);
      throw error;
    }
  }
};

// Chat Agent interfaces
export interface ChatSessionResponse {
  id: string;
  object: string;
  status: string;
  chatkit_configuration: {
    automatic_thread_titling: {
      enabled: boolean;
    };
    file_upload: {
      enabled: boolean;
      max_file_size: number;
      max_files: number;
    };
    history: {
      enabled: boolean;
      recent_threads: any;
    };
  };
  client_secret: string;
  expires_at: number;
  max_requests_per_1_minute: number;
  rate_limits: {
    max_requests_per_1_minute: number;
  };
  user: string;
  workflow: {
    id: string;
    state_variables: any;
    tracing: {
      enabled: boolean;
    };
    version: any;
  };
}

/**
 * Create a chat session and get client secret for ChatKit
 * @param idToken - User's authentication token
 * @param workflowId - The workflow ID to use for the chat session (optional, defaults to Daisy AI workflow)
 * @returns Chat session response with client_secret and workflow ID
 */
export const createChatSession = async (
  idToken: string,
  workflowId: string = "wf_68e650111e3481909390d49ce8f1da200c76078071fdb14c"
): Promise<ChatSessionResponse> => {
  if (!idToken) {
    throw new Error("Authentication token (idToken) is required.");
  }

  // Extract the real Cognito user ID from the token
  const userId = CognitoAuthService.getUserId(idToken);
  debug.log("Extracted Cognito user ID:", userId);
  if (!userId) {
    throw new Error("Unable to extract user ID from authentication token.");
  }

  try {
    const response = await axios.post(
      `${BASE_URL}/chat`,
      {
        workflow: { id: workflowId },
        user: userId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.data) {
      throw new Error("No data received from chat session API");
    }

    const data: ChatSessionResponse = response.data;
    debug.log("Chat session created:", {
      id: data.id,
      user_id: userId,
      workflow_id: data.workflow.id,
      expires_at: new Date(data.expires_at * 1000).toISOString()
    });

    return data;
  } catch (error) {
    debug.error("Error creating chat session:", error);
    throw error;
  }
};

/**
 * Invoke agent tool/function via backend Lambda
 * This is called when ChatKit invokes a client-side tool (e.g., generate_ideogram_prompt)
 * @param agentId - The agent ID to invoke (e.g., "ai_agent_ideogram_001")
 * @param input - The input text/query for the agent
 * @param customerId - Optional customer ID for validation
 * @param apiKey - API key for authentication (optional, uses env var if not provided)
 * @returns The agent's response with output, agent_id, and agent_name
 */
export const invokeAgentTool = async (
  agentId: string,
  input: string,
  customerId?: string,
  apiKey?: string
): Promise<{ output: string; agent_id: string; agent_name: string }> => {
  const endpoint = "https://5r8uvbyti7.execute-api.us-east-1.amazonaws.com/Prod/invoke-agent";
  const key = apiKey || process.env.REACT_APP_AGENT_API_KEY;

  if (!key) {
    throw new Error("API key is required. Set REACT_APP_AGENT_API_KEY environment variable.");
  }

  const payload: {
    agent_id: string;
    customer_id?: string;
    input: string;
  } = {
    agent_id: agentId,
    input: input,
  };

  if (customerId) {
    payload.customer_id = customerId;
  }

  try {
    debug.log("🚀 Invoking agent tool:", { 
      agentId, 
      customerId, 
      inputLength: input.length,
      endpoint,
      hasApiKey: !!key 
    });
    debug.log("📦 Payload:", payload);
    
    const response = await axios.post(endpoint, payload, {
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": key,
      },
      // Add timeout
      timeout: 30000, // 30 seconds
    });

    debug.log("✅ Response status:", response.status);
    debug.log("✅ Response headers:", response.headers);
    debug.log("✅ Response data:", response.data);

    if (response.status !== 200) {
      throw new Error(`Backend returned status ${response.status}`);
    }

    return response.data;
  } catch (error: any) {
    debug.error("❌ Error invoking agent tool:", error);
    
    // Detailed error logging
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      debug.error("🌐 Network Error Details:", {
        message: error.message,
        code: error.code,
        endpoint: endpoint,
        isCorsError: error.message.includes('CORS') || error.message.includes('Access-Control'),
      });
      
      const corsError = "CORS Error: The backend API Gateway endpoint needs to allow requests from http://localhost:3000. " +
        "Please configure CORS on the API Gateway endpoint to allow: " +
        "Origin: http://localhost:3000, Methods: POST, OPTIONS, Headers: Content-Type, X-API-Key";
      
      throw new Error(corsError);
    }
    
    if (error.response) {
      debug.error("📡 Response Error:", {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        data: error.response.data,
      });
    }
    
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        "Failed to invoke agent tool";
    throw new Error(errorMessage);
  }
};